import { Pages } from 'modules/navigation/domain/enums/pages';
import { Team } from 'modules/teams/domain/interfaces/team';
import { useTeamList } from 'modules/teams/ui/hooks/use-teams-list';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { TEAM_DEFAULT_LOGO } from 'shared/constants/links';
import { UDListItem, UDText } from 'ud-ui/components';
import UDButton from 'ud-ui/components/button';
import HeaderContentCategory from 'ud-ui/components/category/components/list-wrapper/header-content';

import CategoryListPage from 'ud-ui/components/category/pages/category-list-page';
import Modal from 'ud-ui/components/modal';
import { useMainLayout } from 'ud-ui/layout/main/context';

type Source = 'common' | 'players';
const naming = {
  searchPlaceholder: 'Поиск по командам',
  addButtonText: 'Добавить команду',
};

const DEFAULT_DELETE_ERROR = 'Не удалось удалить команду';

const getDeleteErrorMessage = (error: unknown): string => {
  const message = typeof error === 'string' ? error : error instanceof Error ? error.message : '';

  if (message.includes('Cannot delete team with matches')) {
    return 'Невозможно удалить команду: у неё есть сыгранные матчи';
  }

  return DEFAULT_DELETE_ERROR;
};

const TeamsPage = () => {
  const navigate = useNavigate();
  const handleCreateClick = useCallback(() => {
    navigate(Pages.TEAMS.CREATE);
  }, [navigate]);
  const { setHeaderContent } = useMainLayout();

  const { isLoading, teams, loadNextPages, changeFilter, deleteTeam, teamsByFilter } = useTeamList();
  const [teamToDelete, setTeamToDelete] = useState<Team | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const openDelete = useCallback((team: Team) => {
    setTeamToDelete(team);
  }, []);
  const closeDelete = useCallback(() => {
    setTeamToDelete(null);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!teamToDelete) return;
    setIsDeleting(true);
    try {
      await deleteTeam(teamToDelete.id);
      setTeamToDelete(null);
      toast.success('Команда успешно удалена');
    } catch (error) {
      toast.error(getDeleteErrorMessage(error));
    } finally {
      setIsDeleting(false);
    }
  }, [deleteTeam, teamToDelete]);

  const handleEditInfoClick = useCallback(
    (team: { id: number }, source: Source) => {
      navigate(Pages.TEAMS.builders.edit(team.id, source));
    },
    [navigate],
  );

  useEffect(() => {
    setHeaderContent(
      <HeaderContentCategory title="Команды" createButtonText="Добавить команду" createButtonOnClick={handleCreateClick} />,
    );
    return () => setHeaderContent(null);
  }, [handleCreateClick, setHeaderContent]);

  return (
    <>
      <CategoryListPage
        categoryName="teams"
        text={naming}
        isLoading={isLoading}
        getNextPages={loadNextPages}
        getFiltered={changeFilter}
        onCreateClick={handleCreateClick}
      >
        {(teamsByFilter || teams).map((team, index) => {
          return (
            <UDListItem
              key={team.id}
              index={index + 1}
              image={team.logo?.url ?? TEAM_DEFAULT_LOGO}
              className="white mb-2"
              title={`${team.name} ${team.shortName ? `(${team.shortName})` : ''}`}
              subtitle={
                <div className="flex w-full justify-between" style={{ fontWeight: 400 }}>
                  <p className="mr-1">Дата создания: {team.createdAt ?? '-'}</p>
                  <p style={{ minWidth: 80 }}>Состав: {team.players.length}</p>
                </div>
              }
              actions={[
                {
                  label: 'Удалить',
                  onClick: () => openDelete(team),
                  variant: 'secondary',
                  style: { width: 144 },
                },
              ]}
              onClick={() => handleEditInfoClick(team, 'common')}
            />
          );
        })}
      </CategoryListPage>

      <Modal show={Boolean(teamToDelete)} onClose={closeDelete}>
        <Modal.Body className={'min-h-10 min-w-100 text-center'}>
          <UDText className="mt-2.5" type="title-1" isNew>
            Удалить команду «{teamToDelete?.name}»?
          </UDText>
          <UDText type="text-1" isNew>
            Это действие необратимо.
          </UDText>
        </Modal.Body>
        <Modal.Footer>
          <div className="flex w-full gap-3">
            <UDButton variant="secondary" onClick={closeDelete} disabled={isDeleting} stretch>
              Отмена
            </UDButton>
            <UDButton variant="primary" onClick={handleConfirmDelete} loading={isDeleting} stretch>
              Удалить
            </UDButton>
          </div>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default TeamsPage;
